import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import { LocationsApi, WorkspacesApi } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { useLocations } from '@/core/hooks/use-locations';
import { useReputationPresets } from '@/core/hooks/use-reputation';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import {
  useCurrentWorkspace,
  useSetWorkspace,
} from '@/core/store/workspace.store';

/**
 * Industry preset: the workspace default plus per-location overrides in one
 * group. `highlightLocationId` is accepted for deep links; overrides are always
 * shown inline, so it needs no extra handling.
 */
export function ReputationSetupAnalysisSection(_props: {
  highlightLocationId?: string;
}) {
  const d = useDictionary().reputation.setup.analysis;
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const setWorkspace = useSetWorkspace();
  const { refetch: refetchWorkspaces } = useWorkspaces();
  const queryClient = useQueryClient();

  const { data: presets = [], isLoading: presetsLoading } =
    useReputationPresets(workspaceId);
  const { locations, isLoading: locationsLoading } = useLocations(workspaceId, {
    limit: 100,
  });

  const overrides = useMemo(
    () => locations.filter((loc) => loc.reputationPreset != null),
    [locations],
  );
  const available = useMemo(
    () => locations.filter((loc) => loc.reputationPreset == null),
    [locations],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [pickLocationId, setPickLocationId] = useState('');
  const [pickPreset, setPickPreset] = useState('');

  const updateWorkspaceMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (defaultReputationPreset: string | null) => {
      if (!workspaceId) return;
      await WorkspacesApi.update({
        path: { id: workspaceId },
        body: { defaultReputationPreset },
      });
    },
    onSuccess: async () => {
      const refreshed = await refetchWorkspaces();
      const updated = refreshed.find((w) => w.id === workspaceId);
      if (updated) setWorkspace(updated);
    },
    onError: () => toast.error(d.workspacePresetError),
  });

  const updateLocationPresetMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async ({
      locationId,
      reputationPreset,
    }: {
      locationId: string;
      reputationPreset: string | null;
    }) => {
      if (!workspaceId) return;
      await LocationsApi.update({
        path: { workspaceId, id: locationId },
        body: { reputationPreset },
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root }),
    onError: () => toast.error(d.locationPresetError),
  });

  const presetSelect = (
    value: string,
    onChange: (next: string) => void,
    disabled: boolean,
    placeholder?: string,
  ) => (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full sm:w-[260px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {presets.map((preset) => (
          <SelectItem key={preset.id} value={preset.id}>
            {preset.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const rowLayout = 'flex flex-wrap items-center gap-x-3.5 gap-y-2.5';

  return (
    <div className="flex flex-col gap-2">
      <SettingsGroup>
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className={rowLayout}>
              <div className="min-w-0 flex-1 basis-56">
                <p className="text-sm font-semibold">
                  {d.workspacePresetTitle}
                </p>
                <p className="text-muted-foreground mt-px text-xs">
                  {d.workspacePresetDescription}
                </p>
              </div>
              {presetsLoading ? (
                <Skeleton className="h-9 w-full sm:w-[260px]" />
              ) : (
                presetSelect(
                  workspace?.defaultReputationPreset ?? '',
                  (value) => {
                    const next = value || null;
                    if (next !== (workspace?.defaultReputationPreset ?? null)) {
                      updateWorkspaceMutation.mutate(next);
                    }
                  },
                  updateWorkspaceMutation.isPending,
                  d.workspacePresetPlaceholder,
                )
              )}
            </div>
          </SettingsGroupRow>

          {locationsLoading ? (
            <SettingsGroupRow separated>
              <Skeleton className="h-9 w-full" />
            </SettingsGroupRow>
          ) : (
            overrides.map((loc) => (
              <SettingsGroupRow key={loc.id} separated>
                <div className={rowLayout}>
                  <div className="flex min-w-0 flex-1 basis-56 items-center gap-2.5">
                    <Badge variant="secondary" className="text-[10.5px]">
                      {d.overrideTag}
                    </Badge>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {loc.name}
                      </p>
                      <p className="text-muted-foreground mt-px text-xs">
                        {d.overrideDescription}
                      </p>
                    </div>
                  </div>
                  {presetSelect(
                    loc.reputationPreset ?? '',
                    (value) =>
                      updateLocationPresetMutation.mutate({
                        locationId: loc.id,
                        reputationPreset: value,
                      }),
                    updateLocationPresetMutation.isPending,
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={d.removeOverride}
                    disabled={updateLocationPresetMutation.isPending}
                    onClick={() =>
                      updateLocationPresetMutation.mutate({
                        locationId: loc.id,
                        reputationPreset: null,
                      })
                    }
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              </SettingsGroupRow>
            ))
          )}

          {addOpen ? (
            <SettingsGroupRow separated>
              <div className={rowLayout}>
                <Select
                  value={pickLocationId}
                  onValueChange={setPickLocationId}
                >
                  <SelectTrigger className="w-full sm:w-[260px]">
                    <SelectValue placeholder={d.pickLocationPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {presetSelect(
                  pickPreset,
                  setPickPreset,
                  false,
                  d.workspacePresetPlaceholder,
                )}
                <Button
                  disabled={
                    !pickLocationId ||
                    !pickPreset ||
                    updateLocationPresetMutation.isPending
                  }
                  onClick={() => {
                    updateLocationPresetMutation.mutate({
                      locationId: pickLocationId,
                      reputationPreset: pickPreset,
                    });
                    setAddOpen(false);
                    setPickLocationId('');
                    setPickPreset('');
                  }}
                >
                  {d.continueOverride}
                </Button>
                <Button variant="outline" onClick={() => setAddOpen(false)}>
                  {d.cancel}
                </Button>
              </div>
            </SettingsGroupRow>
          ) : (
            <SettingsGroupRow separated>
              <Button
                variant="ghostAccent"
                size="sm"
                disabled={available.length === 0 || presetsLoading}
                onClick={() => setAddOpen(true)}
              >
                <Plus className="mr-1.5 size-4" aria-hidden />
                {d.addOverride}
              </Button>
            </SettingsGroupRow>
          )}
        </SettingsGroupRows>
      </SettingsGroup>

      <p className="text-muted-foreground flex gap-2 px-1 text-xs">
        <Clock className="mt-px size-3.5 shrink-0" aria-hidden />
        {d.reanalysisWarning}
      </p>
    </div>
  );
}
